import { Roles } from '@consts/role';
import { UserContent } from '@models/user-contents';
import { cdnImageUrl } from '@utils/image';
import { ReactComponent as HeartIcon } from '@assets/images/app/heart-nutral.svg';
import * as cheerio from 'cheerio';
import moment from 'moment';
import 'moment/locale/ko';
import React from 'react';

moment.locale('ko');

export interface UserContentSummaryProps {
  item: UserContent;
  loungeCurationCategoriesDict?: { [key: string]: string | undefined };
  textOnly?: boolean;
  bestBadgeOnly?: boolean;
  imageSize?: string;
  imagePosition?: string;
  hideName?: boolean;
}

export const UserContentSummary: React.FC<UserContentSummaryProps> = (props) => {
  const userContent = props.item;
  const loungeCurationCategoriesDict = props.loungeCurationCategoriesDict;
  const imageSize = props.imageSize || '43px';
  const imagePosition = props.imagePosition || 'right';
  const like = userContent?.like !== undefined && userContent?.like > 0;

  if (!userContent) {
    return <></>;
  }
  let suffix = '';
  if ((userContent.publisher?.roles || []).includes(Roles.PARENT)) {
    suffix = ' 부모님';
  }

  const htmlText = userContent.text;
  const dom = cheerio.load(htmlText);
  const title = dom('h1').text();
  dom('h1').remove();
  const description = dom('body').text();
  const board = userContent.board;
  let badgeText: string | undefined = '후기';
  let badgeBackgroundColor = '#EBF9FF';
  let badgeColor = '#1890FF';
  if (board !== 'review' && userContent.category) {
    badgeText = loungeCurationCategoriesDict?.[userContent.category];
    badgeBackgroundColor = '#F5F7FB';
    badgeColor = '#808387';
  }

  if (userContent.bestDate) {
    badgeText = '베스트';
    badgeBackgroundColor = '#FFF0F6';
    badgeColor = '#FF3D8F';
  }

  const renderImage = () => {
    if (!props.textOnly && userContent.images && userContent.images.length > 0) {
      return (
        <div
          style={{
            width: imageSize,
            height: imageSize,
            borderRadius: '8px',
            border: '1px solid #F8F9FC',
            overflow: 'hidden',
            flexShrink: 0,
            position: 'relative',
          }}>
          {userContent.images?.length > 1 && (
            <div
              style={{
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'absolute',
                top: 0,
                left: 0,
                borderRadius: '8px 0',
                backgroundColor: 'rgba(0, 0, 0, 0.40)',
                color: '#fff',
                fontFamily: 'Pretendard',
                fontSize: '14px',
                fontWeight: 500,
              }}>
              {userContent.images.length}
            </div>
          )}
          <img
            src={cdnImageUrl(userContent.images[0])}
            alt="first"
            style={{ width: imageSize, height: imageSize, objectFit: 'cover' }}
          />
        </div>
      );
    }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', minWidth: 0, justifyContent: 'space-between', gap: '12px', minHeight: imageSize }}>
        {imagePosition === 'left' && renderImage()}
        <div style={{ minWidth: 0, flexGrow: 1 }}>
          {title && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>
              {badgeText && (
                <div
                  style={{
                    borderRadius: '4px',
                    background: badgeBackgroundColor,
                    color: badgeColor,
                    fontFamily: 'Pretendard',
                    fontSize: '12px',
                    fontStyle: 'normal',
                    fontWeight: 600,
                    padding: '2px 6px',
                    flexShrink: 0,
                    lineHeight: 'normal',
                  }}>
                  {badgeText}
                </div>
              )}
              <div
                style={{
                  wordBreak: 'break-word',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  color: 'var(--Neutral-90, #424242)',
                  fontFamily: 'Pretendard',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 600,
                }}>
                {title}
              </div>
            </div>
          )}
          <div
            style={{
              display: '-webkit-box',
              maxWidth: '100%',
              maxHeight: '60px',
              margin: '0 auto',
              fontSize: '14px',
              WebkitLineClamp: '3',
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              wordBreak: 'break-all',
              color: 'var(--Neutral-80, #66686B)',
              whiteSpace: 'pre-wrap',
              fontFamily: 'Pretendard',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '20px',
            }}>
            {!title && badgeText && (
              <span
                style={{
                  borderRadius: '4px',
                  background: badgeBackgroundColor,
                  color: badgeColor,
                  fontFamily: 'Pretendard',
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  padding: '2px 6px',
                  flexShrink: 0,
                  marginRight: '4px',
                  height: '18px',
                }}>
                {badgeText}
              </span>
            )}
            {description}
          </div>
        </div>
        {imagePosition !== 'left' && renderImage()}
      </div>
      {!props.textOnly && !props.hideName && (
        <div
          style={{
            color: 'var(--Neutral-60, #9B9DA0)',
            fontFamily: 'Pretendard',
            fontSize: '13px',
            fontStyle: 'normal',
            fontWeight: 400,
            display: 'flex',
            alignItems: 'center',
            lineHeight: '18px',
          }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {`${userContent.publisher?.nickname + suffix} · ${moment(userContent.updatedAt).fromNow()}`}
            {like && (
              <div style={{ display: 'flex', alignItems: 'center', marginLeft: '8px' }}>
                <HeartIcon />
                {userContent?.like}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserContentSummary;
